import React, { useEffect } from 'react';
import { Typography, Input, Form, theme } from 'antd';
import { ChargeBeePlanId, ChargeBeePlan, getPlanById } from '@prerender/billing-shared';

import { ModalTemplate } from '../../FreeTrial/components/ModalTemplate';
import { useTrial } from '../../FreeTrial/useTrial';
import useTrackBillingEvent from '../hooks/useTrackBillingEvent';

const { TextArea } = Input;

function TrialCopy({ requestedPlan }: { requestedPlan: string }) {
  const { token } = theme.useToken();

  return (
    <div style={{ marginBottom: token.marginLG }}>
      <Typography.Paragraph>
        Your upgrade to the {requestedPlan} plan is now being applied immediately.
      </Typography.Paragraph>

      <Typography.Paragraph>
        This change ends your trial and starts billing for the {requestedPlan} plan right away. If anything looks off,
        contact our Customer Success Team at{' '}
        <Typography.Link target="_blank" href="mailto:support@prerender.io">
          support@prerender.io
        </Typography.Link>
        .
      </Typography.Paragraph>
    </div>
  );
}

function FeedbackPlanChangedModal({
  open,
  planIds,
  onClose,
}: {
  open: boolean;
  planIds: { old: ChargeBeePlanId; new: ChargeBeePlanId };
  onClose?: () => void;
}) {
  const { isInTrial, hadTrialBefore } = useTrial();
  const trackBillingEvent = useTrackBillingEvent();
  const { token } = theme.useToken();

  let oldPlan: ChargeBeePlan | undefined;
  let newPlan: ChargeBeePlan | undefined;
  try {
    oldPlan = getPlanById(planIds.old);
    newPlan = getPlanById(planIds.new);
  } catch {
    // Plan not in billing-shared — frontend bundle may be stale
  }

  const plansResolved = !!oldPlan && !!newPlan;

  useEffect(() => {
    if (!plansResolved) onClose?.();
  }, [plansResolved, onClose]);

  const onSubmitFeedback = (values: { reason: string }) => {
    trackBillingEvent('PlanChangedFeedback', {
      reason: values.reason,
      had_trial: hadTrialBefore,
      old_plan: planIds.old,
      new_plan: planIds.new,
    });
    onClose?.();
  };

  const onCancel = () => {
    trackBillingEvent('PlanChangedFeedback', {
      reason: '_skipped_',
      had_trial: hadTrialBefore,
      old_plan: planIds.old,
      new_plan: planIds.new,
    });
    onClose?.();
  };

  if (!oldPlan || !newPlan) return null;

  const isDowngrading = oldPlan.index > newPlan.index;

  return (
    <ModalTemplate
      open={open}
      onCancel={onCancel}
      okText="Submit feedback"
      okButtonProps={{ htmlType: 'submit', form: 'converted' }}
      cancelText="Close"
      cancelButtonProps={{ type: 'text' }}
      title={`${isDowngrading ? 'Downgrade' : 'Upgrade'} to ${newPlan.name} plan`}
    >
      <div style={{ marginBottom: token.marginLG }}>
        {isInTrial && <TrialCopy requestedPlan={newPlan.name} />}
        {!isInTrial && !isDowngrading && (
          <Typography.Paragraph>
            We&apos;re thrilled to see you enjoying the full power of Prerender.
          </Typography.Paragraph>
        )}
        <Typography.Paragraph>
          Your plan {isDowngrading ? 'downgrade' : 'upgrade'} is in progress and may take a few moments to appear. If it
          doesn&apos;t show right away, please refresh the page.
        </Typography.Paragraph>
      </div>
      <Typography.Text strong>
        To help us serve you better, we&apos;d love your feedback. Could you let us know why you chose to{' '}
        {isDowngrading ? 'downgrade' : 'upgrade'}?
      </Typography.Text>
      <Form name="converted" onFinish={onSubmitFeedback}>
        <Form.Item name="reason" rules={[{ required: true, message: 'Please provide your feedback' }]}>
          <TextArea
            style={{ marginTop: token.margin }}
            placeholder="Type here"
            autoSize={{ minRows: 5 }}
            maxLength={255}
            showCount
          />
        </Form.Item>
      </Form>
    </ModalTemplate>
  );
}

export default FeedbackPlanChangedModal;
